import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsSubscriptionComponent } from './Pages/alerts-subscription/alerts-subscription.component';
import { EmailSubscriptionComponent } from './Pages/email-subscription/email-subscription.component';
import { IdentityComponent } from './Pages/identity/identity.component';
import { PrimaryFilterComponent } from 'src/app/shared/components/primary-filter/primary-filter.component';
import { SubHeaderComponent } from 'src/app/components/sub-header/sub-header.component';
import { PrimaryFilterItem } from 'src/app/shared/components/primary-filter/models/primary-filter.model';
import { INRSelectOption } from 'src/app/shared/interfaces/nr-select-option.interface';
import { ComponentTypeEnum } from 'src/app/shared/enums/component-type.enum';
import {
  AlertCategory,
  ProfilePreferencePayload,
  ProfileResponse,
  Region,
  SubscriptionPreference,
} from './Interfaces/profile';
import { SECTION_LABELS } from '../../core/constants/profile.constants';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { RegionRouteService } from 'src/app/services/shared/nr-region-route.service';
import { API_STATUS_MESSAGES, POPUP } from 'src/app/core/properties/application.properties';
import { PopupService } from 'src/app/shared/components/popup/services/popup.service';
import { AuthService } from 'src/app/services/auth.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { BannerService } from 'src/app/shared/components/nr-banner/banner.service';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';
import { canDeactivate } from '../../services/utility/navigation-utility';
import { CatCounterService } from 'src/app/services/cat-counter.service';

enum ProfileFilterCompIdsEnum {
  region = 'region',
  route = 'route',
}

@Component({
  selector: 'profile',
  imports: [
    AlertsSubscriptionComponent,
    EmailSubscriptionComponent,
    SubHeaderComponent,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    IdentityComponent,
    PrimaryFilterComponent,
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent implements CanComponentDeactivate, OnInit {
  isFirstLogin = false;
  disableSaveBtn = true;
  disableCancelBtn = true;
  routeChanged = false;
  alertChanged = false;
  subscriptionChanged = false;
  initialRouteId: number | null | undefined = null;
  initialRegionId: number | null | undefined = null;
  profilePreference!: ProfileResponse;
  filterItems: PrimaryFilterItem[] = [];
  selectedRegion!: INRSelectOption;
  selectedRoute!: INRSelectOption;
  regionDropdown: Region[] = [];
  routeDropdown: Region[] = [];
  profileForm!: FormGroup;
  profilePreferencePayloadData: ProfilePreferencePayload = {
    regionId: 0,
    routeId: 0,
    alertPreference: [],
    subscriptionPreference: [],
  };
  alertPreferenceData: AlertCategory[] | undefined;
  emailSubscriptionPreferenceData: SubscriptionPreference[] | undefined;
  initialAlertPreferenceData: AlertCategory[] | undefined;
  initialEmailSubscriptionPreferenceData: SubscriptionPreference[] | undefined;
  defaultPreferences = {
    alertPreference: [],
    subscriptionPreference: [],
  };
  activeAccountDetails: ProfileResponse['user'] | null = null;
  originalEmailSubscriptionPreferenceData: SubscriptionPreference[] = [];
  sectionLabels = SECTION_LABELS;
  isAdmin = false;
  private readonly authService = inject(AuthService);
  private readonly profileService = inject(ProfileService);
  private readonly fb = inject(FormBuilder);
  private readonly userAuthService = inject(UserAuthService);
  private readonly bannerService = inject(BannerService);
  private readonly popupSvc = inject(PopupService);
  private readonly regionRouteService = inject(RegionRouteService);
  private readonly modalService = inject(NgbModal);
  private readonly catCounterService = inject(CatCounterService);

  ngOnInit(): void {
    this.profileForm = this.fb.group({
      preferences: this.fb.array([]),
      subscriptions: this.fb.array([]),
    });

    this.userAuthService.user$.subscribe((isReady) => {
      this.isFirstLogin = isReady.firstLogin;
      this.regionRouteService.getSelectedRoute$().subscribe((data) => {
        this.selectedRoute = data;
      });
      this.loadUserPreferences();
    });
    this.regionRouteService.loadSetRegionRoute(null, null);
    this.authService.userDetails$.subscribe((userDetails) => {
      if (userDetails) {
        this.activeAccountDetails = userDetails;
      }
    });
    this.authService.isAdmin$?.subscribe((isAdmin) => {
      if (isAdmin) {
        this.isAdmin = isAdmin;
      }
    });
    this.regionRouteService.getRegionDropdown$().subscribe((data) => {
      this.regionDropdown = data;
      this.createFilterData();
    });
    this.regionRouteService.getRouteDropdown$().subscribe((data) => {
      this.routeDropdown = data;
      this.createFilterData();
    });
    this.regionRouteService.getSelectedRegion$().subscribe((data) => {
      this.selectedRegion = data;
      this.createFilterData();
    });
  }

  loadUserPreferences() {
    this.userAuthService.userProfileData$.subscribe((userProfileData) => {
      this.profilePreference = userProfileData;
      if (this.profilePreference) {
        this.originalEmailSubscriptionPreferenceData = _.cloneDeep(
          this.profilePreference.subscriptionPreference,
        );
        this.regionRouteService.loadSetRegionRoute(
          this.profilePreference.regionId,
          this.profilePreference.routeId,
        );
        this.createPreferenceForm();
        this.createSubscriptionForm();
        this.initialRegionId = this.profilePreference.regionId;
        this.initialRouteId = this.profilePreference.routeId;
        this.alertChanged = false;
        this.subscriptionChanged = false;
        this.disableSaveBtn = true;
        this.disableCancelBtn = true;
      }
    });
  }

  createFilterData(): void {
    const { region, route } = ProfileFilterCompIdsEnum;
    const { Dropdown } = ComponentTypeEnum;
    this.filterItems = [
      {
        id: region,
        label: 'Region',
        data: this.regionDropdown,
        optionLabel: 'name',
        optionValue: 'id',
        selectedItems: this.selectedRegion,
        type: Dropdown,
        validation: { isRequired: true, isRequiredMsg: 'Select Region' },
        hasDependency: true,
        ariaLabel: 'Select Region',
        autofocus: true,
        filter: true,
      },
      {
        id: route,
        label: 'Route',
        data: this.routeDropdown,
        optionLabel: 'name',
        optionValue: 'id',
        selectedItems: this.selectedRoute,
        type: Dropdown,
        validation: { isRequired: true, isRequiredMsg: 'Select Route'},
        ariaLabel: 'Select Route',
        filter: true,
        disabled: !this.routeDropdown?.length,
      },
    ];
  }

  get preferences(): FormArray<FormGroup> {
    return this.profileForm.get('preferences') as FormArray;
  }

  get subscriptions(): FormArray<FormGroup> {
    return this.profileForm.get('subscriptions') as FormArray;
  }

  createPreferenceForm(): void {
    this.profilePreference?.alertPreference?.categories?.forEach(
      ({ alertCategory, preferences }) => {
        this.initialAlertPreferenceData = this.profilePreference?.alertPreference?.categories;
        const categoryGroup = this.fb.group({
          alertCategory: [alertCategory],
          preferences: this.fb.array(
            preferences.map(({ alertDescription, isActive }) =>
              this.fb.group({
                alertDescription: [alertDescription],
                isActive: [isActive],
              }),
            ),
          ),
        });

        this.preferences.push(categoryGroup);
      },
    );
  }

  createSubscriptionForm(): void {
    this.profilePreference?.subscriptionPreference?.forEach(
      ({ subscriptionId, subDescription, isActive }) => {
        this.initialEmailSubscriptionPreferenceData =
          this.profilePreference?.subscriptionPreference;
        this.subscriptions.push(
          this.fb.group({
            subscriptionId: [subscriptionId],
            subDescription: [subDescription],
            isActive: [isActive],
          }),
        );
      },
    );
  }

  performCancelLogic(): void {
    // 1. Rebuild Preferences FormArray
    const newPreferenceArray = new FormArray<FormGroup>([]);
    this.profilePreference.alertPreference.categories.forEach(({ alertCategory, preferences }) => {
      const categoryGroup = this.fb.group({
        alertCategory: [alertCategory],
        preferences: this.fb.array(
          preferences.map(({ alertDescription, isActive }) =>
            this.fb.group({
              alertDescription: [alertDescription],
              isActive: [isActive],
            }),
          ),
        ),
      });
      newPreferenceArray.push(categoryGroup);
    });
    this.profileForm.setControl('preferences', newPreferenceArray);

    // 2. Rebuild Subscriptions FormArray
    const newSubscriptionArray = this.fb.array(
      this.profilePreference.subscriptionPreference.map(
        ({ subscriptionId, subDescription, isActive }) =>
          this.fb.group({
            subscriptionId: [subscriptionId],
            subDescription: [subDescription],
            isActive: [isActive],
          }),
      ),
    );
    this.profileForm.setControl('subscriptions', newSubscriptionArray);

    this.profilePreference.alertPreference.categories = _.cloneDeep(
      this.initialAlertPreferenceData,
    );
    this.profilePreference.subscriptionPreference = _.cloneDeep(
      this.initialEmailSubscriptionPreferenceData,
    );
    this.originalEmailSubscriptionPreferenceData = _.cloneDeep(
      this.initialEmailSubscriptionPreferenceData,
    );

    if (this.profilePreference.regionId && this.profilePreference.routeId) {
      this.regionRouteService.loadSetRegionRoute(
        this.profilePreference['regionId'],
        this.profilePreference.routeId,
      );
    }

    if (this.profilePreference.regionId === null && this.profilePreference.routeId === null) {
      this.routeDropdown = []; // Optionally reset
      this.createFilterData();
    } else {
      this.regionRouteService.getRouteDropdown$().subscribe((data) => {
        this.routeDropdown = data;
        this.selectedRoute =
          this.routeDropdown.find((route) => route.id === this.profilePreference.routeId) ?? null;
        this.createFilterData();
      });
    }
    this.alertChanged = false;
    this.subscriptionChanged = false;
    this.routeChanged = false;
    this.disableCancelBtn = true;
    this.disableSaveBtn = true;
  }

  onChangeEvtHandler(event): void {
    const changedItem = event?.changedItem;
    const changedItemId = changedItem?.id;
    const selectedItemId = changedItem?.selectedItems?.id;

    if (!changedItemId) return;

    // Handle updates based on changed item type
    if (changedItemId === 'region') {
      this.regionRouteService.updateRegion(selectedItemId);
    } else if (changedItemId === 'route') {
      this.regionRouteService.updateRoute(selectedItemId);
    }

    if (changedItemId === 'region') {
      // Always disable buttons when region is changed
      this.disableSaveBtn = true;
      this.disableCancelBtn = true;
      return;
    }

    if (changedItemId === ProfileFilterCompIdsEnum.route) {
      const isRegionDefault = this.selectedRegion?.id === this.initialRegionId;
      const isRouteDefault = this.selectedRoute?.id === this.initialRouteId;
      const isDefaultState = isRegionDefault && isRouteDefault;
      this.disableSaveBtn = isDefaultState;
      this.disableCancelBtn = isDefaultState;
    }
  }

  handleAlertPreferenceChange(alertPreferenceData): void {
    const originalCategories = this.initialAlertPreferenceData;
    if (!originalCategories || !alertPreferenceData) {
      console.error('Missing data for comparison');
      return;
    }
    const changedPreferences = [];
    let hasChanges = false;
    // Iterate through the original categories and compare with alertPreferenceData
    originalCategories.forEach((originalCategory, catIdx: number) => {
      const updatedCategory = alertPreferenceData[catIdx];
      if (!updatedCategory) {
        console.warn(`No corresponding category found for index ${catIdx}`);
        return;
      }

      originalCategory.preferences.forEach((originalPref, prefIdx: number) => {
        const updatedPref = updatedCategory.preferences[prefIdx];
        if (!updatedPref) {
          console.warn(`No corresponding preference found for index ${prefIdx}`);
          return;
        }
        // Compare preferences using Lodash's _.isEqual
        if (!_.isEqual(originalPref.isActive, updatedPref.isActive)) {
          hasChanges = true;
          const changedPreference = {
            alertId: originalPref['alertId'] || updatedPref.alertID,
            isActive: updatedPref.isActive,
          };
          if (!this.isFirstLogin) {
            changedPreference['alertPreferenceId'] = originalPref['alertPreferenceId'];
          }
          changedPreferences.push(changedPreference);
        }
      });
    });
    this.profilePreferencePayloadData = {
      ...this.profilePreferencePayloadData,
      alertPreference: changedPreferences,
    };
    this.alertChanged = hasChanges;
    const isRegionChanged = this.selectedRegion?.id !== this.initialRegionId;
    const isRouteChanged = this.selectedRoute?.id !== this.initialRouteId;
    const hasStateChanged = isRegionChanged || isRouteChanged;
    if (this.alertChanged || this.subscriptionChanged || hasStateChanged) {
      this.disableSaveBtn = false;
      this.disableCancelBtn = false;
    } else {
      this.disableSaveBtn = true;
      this.disableCancelBtn = true;
    }
  }
  handleEmailPreferenceChange(emailSubscriptionPreferenceData): void {
    const changedSubscriptions = []; // Use the stable original data for comparison
    const originalEmailSubscriptions = this.originalEmailSubscriptionPreferenceData;
    if (!originalEmailSubscriptions || !emailSubscriptionPreferenceData) {
      console.error('Missing data for comparison');
      return;
    }
    let hasChanges = false;
    // Compare each subscription with its original state
    emailSubscriptionPreferenceData.forEach((updatedSubscription) => {
      const originalSubscription = originalEmailSubscriptions.find(
        (item) => item.subscriptionId === updatedSubscription.subscriptionId,
      );
      if (!originalSubscription) {
        console.warn(`No original subscription found for ID ${updatedSubscription.subscriptionId}`);
        return;
      }
      // If the current state differs from the original, mark as changed
      if (!_.isEqual(originalSubscription.isActive, updatedSubscription.isActive)) {
        hasChanges = true;
        const changedPreference = {
          subscriptionId: updatedSubscription.subscriptionId,
          isActive: updatedSubscription.isActive,
        };
        if (!this.isFirstLogin) {
          changedPreference['subscriptionPreferenceId'] =
            originalSubscription.subscriptionPreferenceId;
        }
        changedSubscriptions.push(changedPreference);
      }
    });
    this.profilePreferencePayloadData = {
      ...this.profilePreferencePayloadData,
      subscriptionPreference: changedSubscriptions,
    };
    this.subscriptionChanged = hasChanges;
    const isRegionChanged = this.selectedRegion?.id !== this.initialRegionId;
    const isRouteChanged = this.selectedRoute?.id !== this.initialRouteId;
    const hasStateChanged = isRegionChanged || isRouteChanged;

    if (this.subscriptionChanged || this.alertChanged || hasStateChanged) {
      this.disableSaveBtn = false;
      this.disableCancelBtn = false;
    } else {
      this.disableSaveBtn = true;
      this.disableCancelBtn = true;
    }
  }

  onSave(): void {
    const basePayload = {
      upnId: this.activeAccountDetails?.id,
      regionId: this.selectedRegion?.id || 0,
      routeId: this.selectedRoute?.id || 0,
      alertPreference: this.profilePreferencePayloadData['alertPreference'] || [],
      subscriptionPreference: this.profilePreferencePayloadData['subscriptionPreference'] || [],
    };

    if (this.isFirstLogin) {
      this.profilePreferencePayloadData = {
        ...basePayload,
        givenName: this.activeAccountDetails?.givenName,
        lastName: this.activeAccountDetails?.surname,
        displayName: this.activeAccountDetails?.displayName,
        emailId: this.activeAccountDetails?.mail,
        userRoleId: this.isAdmin ? 1 : 0,
      };
      this.profileService
        .saveProfilePreferences(this.profilePreferencePayloadData)
        .subscribe((response) => {
          const isSuccess = response === API_STATUS_MESSAGES.PROFILE_PREFRENCE_STATUS_SUCCESS;
          this.bannerService.showBanner('Your profile has been saved successfully', '', 'success');
          this.setButtonState(isSuccess);
          if (response) {
            this.alignUserPreferences(basePayload, this.isFirstLogin);
            // Refresh CAT counter for the new / updated route
            this.catCounterService.refreshCatCount([this.selectedRoute.id], false);
          }
        });
    } else {
      this.profilePreferencePayloadData = basePayload;
      this.profileService
        .updateProfilePreferences(this.profilePreferencePayloadData)
        .subscribe((response) => {
          const isSuccess = response === API_STATUS_MESSAGES.PROFILE_PREFRENCE_STATUS_SUCCESS;
          this.bannerService.showBanner(
            'Your profile has been updated successfully',
            '',
            'success',
          );
          this.regionRouteService.setSelectedRouteIds([this.selectedRoute.id]);
          this.setButtonState(isSuccess);
          if (response) {
            this.alignUserPreferences(basePayload);
             // Update user$ with new routeId
             const updatedUser = {
              ...this.userAuthService?.user,
              routeId: this.selectedRoute?.id,
            };
            this.userAuthService?.setUser(updatedUser);
            // Refresh CAT counter for the new / updated route
             this.catCounterService.refreshCatCount([this.selectedRoute.id], false);
          }
        });
    }
  }

  /** open information popup
   *  when a new user is logged in first time and directly navigating to the other features
   */
  openAlertModal() {
    this.popupSvc.information(POPUP.MESSAGES.USER_ALERT_TEXT);
  }

  // Track if there are unsaved changes
  hasUnsavedChanges(): boolean {
    return !this.disableSaveBtn;
  }

  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    return canDeactivate(() => this.hasUnsavedChanges(), this.modalService);
  }

  setButtonState(disable: boolean): void {
    this.disableSaveBtn = !disable;
    this.disableCancelBtn = !disable;
  }

  onCancel(): void {
    this.popupSvc
      .confirmation(['Your changes will be lost. Do you want to continue?'])
      .then((confirmed: boolean) => {
        if (confirmed) {
          this.performCancelLogic();
        }
      })
      .catch(() => {
        // No action needed if user dismisses the popup
      });
  }

  alignUserPreferences(basePayload: ProfilePreferencePayload, firstLogin?:boolean): void {
    this.profilePreference.alertPreference.categories.forEach((category) => {
      category.preferences.forEach((pref) => {
        const matchingAlert = basePayload.alertPreference.find(
          (base) => base.alertId === pref.alertId,
        );
        if (matchingAlert) {
          pref.isActive = matchingAlert.isActive;
        }
      });
    });

    // Sync subscriptionPreference isActive
    this.profilePreference.subscriptionPreference.forEach((sub) => {
      const matchingSub = basePayload.subscriptionPreference.find(
        (base) => base.subscriptionId === sub.subscriptionId,
      );
      if (matchingSub) {
        sub.isActive = matchingSub.isActive;
      }
    });
    this.profilePreference.regionId = basePayload.regionId;
    this.profilePreference.routeId = basePayload.routeId;
    if(firstLogin){
      window.location.reload();
    }
  }
}
